(function () {
  const SOURCE_LANGUAGE = baseLanguage((window.fkWeglotDynamicConfig && window.fkWeglotDynamicConfig.sourceLanguage) || 'hr');
  const DYNAMIC_SELECTOR = '[data-weglot-dynamic], [data-weglot-dynamic-attributes]';
  const CACHE = new Map();
  const RETRY_LIMIT = 120;
  const RETRY_DELAY = 250;
  let observer = null;
  let refreshTimer = null;
  let retryCount = 0;
  let isBound = false;

  function getWeglot() {
    return window.Weglot || null;
  }

  function baseLanguage(language) {
    return String(language || '').toLowerCase().split('-')[0];
  }

  function getCurrentLanguage() {
    const Weglot = getWeglot();

    if (Weglot && typeof Weglot.getCurrentLang === 'function') {
      return Weglot.getCurrentLang();
    }

    return document.documentElement.lang || SOURCE_LANGUAGE;
  }

  function isSourceLanguage(language) {
    return baseLanguage(language) === SOURCE_LANGUAGE;
  }

  function isWeglotReady() {
    const Weglot = getWeglot();

    return Weglot && typeof Weglot.translate === 'function' && typeof Weglot.getCurrentLang === 'function';
  }

  function sourceAttributeName(attribute) {
    return `data-weglot-source-${attribute}`;
  }

  function setText(element, value) {
    if (element.textContent !== value) element.textContent = value;
  }

  function setAttribute(element, attribute, value) {
    if (element.getAttribute(attribute) !== value) element.setAttribute(attribute, value);
  }

  function getTextSource(element) {
    const source = element.getAttribute('data-weglot-source') || element.textContent.trim();

    if (source && !element.hasAttribute('data-weglot-source')) {
      element.setAttribute('data-weglot-source', source);
    }

    return source;
  }

  function getAttributeSource(element, attribute) {
    const sourceAttribute = sourceAttributeName(attribute);
    const source = element.getAttribute(sourceAttribute) || element.getAttribute(attribute) || '';

    if (source && !element.hasAttribute(sourceAttribute)) {
      element.setAttribute(sourceAttribute, source);
    }

    return source;
  }

  function getDynamicAttributes(element) {
    return (element.getAttribute('data-weglot-dynamic-attributes') || '')
      .split(',')
      .map((attribute) => attribute.trim())
      .filter(Boolean);
  }

  function getDynamicNodes(root) {
    const nodes = [];

    if (root.nodeType !== Node.ELEMENT_NODE) return nodes;
    if (root.matches(DYNAMIC_SELECTOR)) nodes.push(root);

    return nodes.concat(Array.from(root.querySelectorAll(DYNAMIC_SELECTOR)));
  }

  function collectRecords(root) {
    return getDynamicNodes(root).reduce((records, element) => {
      if (element.closest('[data-weglot-no-translate]')) return records;

      if (element.hasAttribute('data-weglot-dynamic')) {
        const source = getTextSource(element);

        if (source) {
          records.push({
            attribute: null,
            element,
            source,
            type: Number(element.getAttribute('data-weglot-type') || 1)
          });
        }
      }

      getDynamicAttributes(element).forEach((attribute) => {
        const source = getAttributeSource(element, attribute);

        if (source) {
          records.push({
            attribute,
            element,
            source,
            type: Number(element.getAttribute(`data-weglot-type-${attribute}`) || 1)
          });
        }
      });

      return records;
    }, []);
  }

  function cacheKey(language, type, source) {
    return `${baseLanguage(language)}:${type}:${source}`;
  }

  function applyRecord(record, value) {
    if (record.attribute) {
      setAttribute(record.element, record.attribute, value);
      return;
    }

    setText(record.element, value);
  }

  function restoreSource(records) {
    records.forEach((record) => applyRecord(record, record.source));
  }

  function readTranslationItem(item) {
    if (typeof item === 'string') return item;
    if (!item || typeof item !== 'object') return '';

    return item.w || item.word || item.translation || item.text || item.value || '';
  }

  function readTranslations(response, originals) {
    const groups = [
      response,
      response && response.to_words,
      response && response.words,
      response && response.translations,
      response && response.data,
      response && response.data && response.data.to_words,
      response && response.data && response.data.words,
      response && response.data && response.data.translations
    ];

    const list = groups.find((group) => Array.isArray(group));

    if (list) {
      return originals.map((source, index) => readTranslationItem(list[index]) || source);
    }

    if (response && typeof response === 'object') {
      return originals.map((source) => response[source] || source);
    }

    return originals;
  }

  function translateRecords(records) {
    const languageTo = getCurrentLanguage();

    if (!records.length) return;
    if (!isWeglotReady() || isSourceLanguage(languageTo)) {
      restoreSource(records);
      return;
    }

    const unique = [];
    const uniqueByKey = new Map();

    records.forEach((record) => {
      const key = cacheKey(languageTo, record.type, record.source);

      if (!uniqueByKey.has(key)) {
        uniqueByKey.set(key, unique.length);
        unique.push({
          key,
          source: record.source,
          type: record.type
        });
      }
    });

    const missing = unique.filter((item) => !CACHE.has(item.key));

    function applyTranslations() {
      records.forEach((record) => {
        const translated = CACHE.get(cacheKey(languageTo, record.type, record.source)) || record.source;
        applyRecord(record, translated);
      });
    }

    if (!missing.length) {
      applyTranslations();
      return;
    }

    const Weglot = getWeglot();
    const payload = {
      languageTo,
      words: missing.map((item) => ({ t: item.type, w: item.source }))
    };

    function handleResponse(response) {
      const translations = readTranslations(
        response,
        missing.map((item) => item.source)
      );

      missing.forEach((item, index) => {
        CACHE.set(item.key, translations[index] || item.source);
      });

      applyTranslations();
    }

    try {
      const result = Weglot.translate(payload, handleResponse);

      if (result && typeof result.then === 'function') {
        result.then(handleResponse).catch(() => restoreSource(records));
      }
    } catch (error) {
      restoreSource(records);
    }
  }

  function refresh(root) {
    const target = root && root.nodeType === Node.ELEMENT_NODE ? root : document.body;

    if (!target) return;
    translateRecords(collectRecords(target));
  }

  function scheduleRefresh(root) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => refresh(root), 40);
  }

  function shouldRefreshForMutation(mutation) {
    if (mutation.type === 'characterData') {
      return Boolean(mutation.target.parentElement && mutation.target.parentElement.closest(DYNAMIC_SELECTOR));
    }

    if (mutation.type === 'attributes') {
      return mutation.target.nodeType === Node.ELEMENT_NODE && mutation.target.closest(DYNAMIC_SELECTOR);
    }

    return Array.from(mutation.addedNodes).some((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;

      return node.matches(DYNAMIC_SELECTOR) || node.querySelector(DYNAMIC_SELECTOR);
    });
  }

  function bindObserver() {
    if (observer || !document.body || !window.MutationObserver) return;

    observer = new MutationObserver((mutations) => {
      if (mutations.some(shouldRefreshForMutation)) scheduleRefresh(document.body);
    });

    observer.observe(document.body, {
      attributeFilter: [
        'aria-label',
        'data-weglot-dynamic',
        'data-weglot-dynamic-attributes',
        'data-weglot-source',
        'placeholder',
        'title'
      ],
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true
    });
  }

  function bindWeglotEvents() {
    const Weglot = getWeglot();

    if (!Weglot || typeof Weglot.on !== 'function') return;

    Weglot.on('initialized', () => scheduleRefresh(document.body));
    Weglot.on('languageChanged', () => scheduleRefresh(document.body));
  }

  function bindDynamicEvents() {
    document.addEventListener('fk:dynamic-content:rendered', (event) => {
      scheduleRefresh((event.detail && event.detail.root) || event.target || document.body);
    });
    document.addEventListener('quickview:loaded', () => scheduleRefresh(document.body));
    document.addEventListener('product-info:loaded', (event) => scheduleRefresh(event.target || document.body));
    window.addEventListener('collectionUpdated', () => scheduleRefresh(document.body));
  }

  function bind() {
    if (isBound || !isWeglotReady()) return;

    isBound = true;
    bindWeglotEvents();
    bindDynamicEvents();
    bindObserver();
    refresh(document.body);
  }

  function waitForWeglot() {
    if (isWeglotReady()) {
      bind();
      return;
    }

    if (retryCount >= RETRY_LIMIT) return;

    retryCount += 1;
    window.setTimeout(waitForWeglot, RETRY_DELAY);
  }

  window.fkWeglotDynamic = {
    refresh,
    sourceLanguage: SOURCE_LANGUAGE
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForWeglot);
  } else {
    waitForWeglot();
  }
})();
